/**
 * Every section of the Settings page a link can open (GET-18882).
 *
 * The one list: each section renders its anchor with `settingsSectionAnchor`,
 * so a section rendering an id that is not here is a type error (the reverse,
 * a listed id nothing renders, is not checked) — and
 * `useSettingsSectionDeepLink` scrolls to `?section=<id>`. Adding a section to
 * this list and spreading its anchor is all it takes to make it linkable.
 *
 * Mirrored by `SETTINGS_SECTIONS` in backend/src/app/utils/app_links.py, which
 * tells the assistant what each one holds; backend/tests/test_app_links.py
 * fails until the two agree. The backend's link plumbing does not read either
 * list: it takes any well-formed slug, and an id this build does not render
 * just opens the top of the page.
 */
export const SETTINGS_SECTIONS = [
  'mail-calendar',
  'additional-inboxes',
  'messaging',
  'assistant-email',
  'automatic-approvals',
  'dedicated-number',
  'purchase-credits',
  'texting-contacts',
  'iphone-action-button',
] as const

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]

/** Query param naming the section; mirrors `SettingsTarget.link_params`. */
export const SETTINGS_SECTION_PARAM = 'section'

export function isSettingsSection(raw: string | null | undefined): raw is SettingsSectionId {
  return (SETTINGS_SECTIONS as readonly string[]).includes(raw ?? '')
}

/**
 * The section a Settings URL's query names, if any.
 *
 * The location-setup link predates sections and is still in chat history as
 * `?location=1`; it names the Shortcut section. One reader, so the scroll and
 * the sign-in routing cannot disagree about where a URL is going.
 */
export function settingsSectionFromSearch(params: URLSearchParams): SettingsSectionId | null {
  const section = params.get(SETTINGS_SECTION_PARAM)
  if (isSettingsSection(section)) return section
  return params.get('location') === '1' ? 'iphone-action-button' : null
}

/** The props that make an element a section's deep-link target. */
export function settingsSectionAnchor(id: SettingsSectionId): { id: SettingsSectionId } {
  return { id }
}
